import React from "react";
import {
  Building2,
  Stethoscope,
  Network,
  Calculator,
  Server,
  CheckCircle2,
  ShieldCheck,
  Lock,
  Gauge,
  Workflow,
} from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Reveal, PageTransition } from "@/components/Reveal";
import { PageHero, CTABand } from "@/components/CTA";
import { Seo } from "@/components/Seo";

const personas = [
  {
    id: "payers",
    icon: Building2,
    label: "Payers",
    headline: "Straight-through claims, fewer manual touches",
    body: "Ingest paper and faxed HCFA/UB-04 claims into your adjudication systems automatically, reduce pended claims, and cut vendor keying costs.",
    outcomes: ["Lower cost per claim", "Faster adjudication", "Fewer data-entry errors", "Audit-ready trails"],
  },
  {
    id: "providers",
    icon: Stethoscope,
    label: "Providers",
    headline: "Capture charges and records without the keystrokes",
    body: "Turn superbills, charts, and faxed referrals into structured data that flows into your EHR and billing systems in seconds.",
    outcomes: ["Faster charge capture", "Cleaner claims out the door", "Less staff burnout", "Searchable records"],
  },
  {
    id: "clearinghouses",
    icon: Network,
    label: "Clearinghouses",
    headline: "Scale document intake without scaling headcount",
    body: "Process high volumes of multi-payer claim forms with consistent accuracy and hand structured data downstream via API.",
    outcomes: ["High-volume throughput", "Consistent accuracy", "API-first delivery", "Flexible routing"],
  },
  {
    id: "rcm",
    icon: Calculator,
    label: "RCM Teams",
    headline: "Protect revenue with faster, cleaner data",
    body: "Reconcile EOBs and ERAs against claims, surface underpayments, and speed appeals with structured remittance data.",
    outcomes: ["Reduced write-offs", "Faster appeals", "Underpayment detection", "Lower DSO"],
  },
  {
    id: "health-it",
    icon: Server,
    label: "Health IT",
    headline: "A document AI layer you can build on",
    body: "Embed Hewto.ai via REST API and webhooks, with security, monitoring, and audit controls your platform team can trust.",
    outcomes: ["REST & webhook APIs", "Role-based access", "Observability", "Dedicated support"],
  },
];

const capabilities = [
  { icon: ShieldCheck, k: "Secure", v: "Encryption & access controls" },
  { icon: Lock, k: "Encryption", v: "In transit and at rest" },
  { icon: Gauge, k: "Millions / mo", v: "Elastic, high-volume throughput" },
  { icon: Workflow, k: "SSO & audit", v: "Advanced access controls" },
];

const Enterprise = () => (
  <PageTransition>
    <Seo
      title="Enterprise Healthcare Document AI | Hewto.ai"
      description="Enterprise-grade document AI for payers, providers, clearinghouses, RCM teams, and health IT — high-volume HCFA, UB-04, and dental claim processing with security and scale."
      keywords="enterprise healthcare document ai, claims automation platform, payer document processing, rcm automation, health it document ai"
      canonical="https://www.hewto.ai/enterprise"
    />
    <PageHero
      eyebrow="Enterprise"
      title="Document AI built for every healthcare team"
      subtitle="From payers to health IT platforms, Hewto.ai delivers the accuracy, security, and scale enterprise healthcare operations require."
    />

    <section className="mx-auto max-w-7xl px-5 py-24 sm:px-8">
      <div className="grid grid-cols-2 gap-6 lg:grid-cols-4">
        {capabilities.map((c, i) => (
          <Reveal key={c.k} delay={i * 0.08}>
            <div className="rounded-2xl border border-slate-200 bg-white p-6">
              <c.icon className="h-6 w-6 text-brand" />
              <div className="mt-4 text-xl font-bold text-slate-900">{c.k}</div>
              <div className="mt-1 text-sm text-slate-600">{c.v}</div>
            </div>
          </Reveal>
        ))}
      </div>
    </section>

    <section className="bg-slate-50 py-24">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal>
          <div className="mx-auto mb-12 max-w-2xl text-center">
            <span className="text-sm font-semibold uppercase tracking-widest text-brand-600">
              Built for your role
            </span>
            <h2 className="mt-4 text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
              Purpose-built for every healthcare team
            </h2>
          </div>
        </Reveal>
        <Tabs defaultValue="payers" className="w-full">
          <TabsList
            data-testid="enterprise-tabs"
            className="mb-10 flex h-auto w-full flex-wrap justify-center gap-2 bg-transparent p-0"
          >
            {personas.map((p) => (
              <TabsTrigger
                key={p.id}
                value={p.id}
                data-testid={`persona-tab-${p.id}`}
                className="flex items-center gap-2 rounded-full border border-slate-200 bg-white px-5 py-2.5 text-sm font-medium text-slate-600 data-[state=active]:border-brand data-[state=active]:bg-brand data-[state=active]:text-white"
              >
                <p.icon className="h-4 w-4" />
                {p.label}
              </TabsTrigger>
            ))}
          </TabsList>

          {personas.map((p) => (
            <TabsContent key={p.id} value={p.id} className="mt-0">
              <div className="mx-auto max-w-3xl rounded-3xl border border-slate-200 bg-white p-10">
                <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand">
                  <p.icon className="h-7 w-7" />
                </span>
                <h3 className="mt-6 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                  {p.headline}
                </h3>
                <p className="mt-4 text-lg leading-relaxed text-slate-600">{p.body}</p>
                <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  {p.outcomes.map((o) => (
                    <div key={o} className="flex items-center gap-2.5 text-sm text-slate-700">
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-brand" />
                      {o}
                    </div>
                  ))}
                </div>
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </div>
    </section>

    <CTABand
      title="Let's scope your enterprise rollout"
      subtitle="Talk to our team about volume options, dedicated infrastructure, and custom model tuning."
    />
  </PageTransition>
);

export default Enterprise;
